import React, { useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { FieldHeader } from 'src/components/Submission/GenericFieldComponents/common/FieldHeader';
import {
  IAvailableWorkflowActions,
  IDependentValue,
  IReviewerRecommendation
} from '@publishing-engine/domain/models';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { EntryPoint } from 'src/utils/types';
import { TextEmptyState } from '../Text/TextEmptyState';
import { ReviewersDisplay } from './ReviewersDisplay';
import { ReviewersDiffModal } from './ReviewersDiffModal';
import { ReviewersUpdateModal } from './ReviewersUpdateModal';
import {
  IReviewersFormField,
  reviewersTypeLabel
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/ReviewerRecommendation';

export type IReviewerValue = IDependentValue<IReviewerRecommendation[]>;

type IProps = {
  formFieldDefinition: IReviewersFormField;
  value: IReviewerValue;
  previousValue?: IReviewerValue;
  availableActions: IAvailableWorkflowActions;
  onChange: (value: IReviewerValue) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
};

export const ReviewersField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isEditable = !!onChange;
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const hasValue = value?.exists === false || value?.exists === true;
  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  const label = reviewersTypeLabel[formFieldDefinition.name].main;

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && (
        <ReviewersUpdateModal
          formField={formFieldDefinition}
          onClose={() => {
            setIsEditing(false);
          }}
          isOpen={isEditing}
          value={value}
          onSubmit={value => {
            setIsEditing(false);
            onChange(value);
          }}
        />
      )}

      {isUpdated && showDiffModal ? (
        <ReviewersDiffModal
          displayLabel={`${label} Reviewers`}
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={`${label} Reviewers`}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {hasValue ? (
        value.exists ? (
          <ReviewersDisplay value={value.value} />
        ) : (
          <Text textStyle="textBody">No</Text>
        )
      ) : (
        <TextEmptyState />
      )}
    </Flex>
  );
};
